import { useState } from "preact/hooks";
import { shareConversation } from "../api/conversations";
import {
  rememberShareLink,
  shareLink,
  type ShareResult,
  shareUrl,
} from "../lib/share";
import {
  conversationId,
  isStreaming,
  markConversationPublic,
  publicConversations,
  rateTurn,
  retryTurn,
  type Turn,
} from "../state";
import { ConfirmDialog } from "./ConfirmDialog";
import { IconRetry, IconShare, IconThumbDown, IconThumbUp } from "./icons";

const STATUS_LABEL: Record<ShareResult, string> = {
  shared: "Shared",
  copied: "Link copied",
  dismissed: "",
  unavailable: "Copy failed",
};

export function AnswerActions({
  turn,
  isLast = false,
}: {
  turn: Turn;
  isLast?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [shareResult, setShareResult] = useState<ShareResult | null>(null);

  async function doShare() {
    const threadId = conversationId.value;
    if (!threadId || busy) return;

    setBusy(true);
    try {
      // Publishing runs concurrently instead of first: `navigator.share` needs
      // the click's user gesture, and awaiting a fetch burns it (Safari then
      // rejects the call). The confirmation dialog already happened, so the
      // link is public long before anyone opens it. Once public, sharing again
      // needs no round-trip at all.
      const publishing = alreadyPublic
        ? Promise.resolve()
        : shareConversation(threadId).then((state) => {
          rememberShareLink(threadId, state.url);
        });

      const result = await shareUrl(shareLink(threadId));

      let failed = false;
      try {
        await publishing;
      } catch (error) {
        console.warn("share failed", error);
        failed = true;
      }
      // The server made it public even if the sheet was dismissed.
      if (!failed) markConversationPublic(threadId);
      if (result === "dismissed" && !failed) return;

      setShareResult(failed ? "unavailable" : result);
      window.setTimeout(() => setShareResult(null), 2000);
    } finally {
      setBusy(false);
    }
  }

  // Once public, sharing again just hands over the link (no repeat warning).
  const alreadyPublic = conversationId.value
    ? publicConversations.value.has(conversationId.value)
    : false;

  return (
    <div class="answer-actions">
      {/* Sharing is per conversation, so it lives on the newest turn only. */}
      {isLast && (
        <button
          type="button"
          class="icon-btn"
          onClick={() => {
            if (alreadyPublic) void doShare();
            else setConfirming(true);
          }}
          disabled={!conversationId.value || busy}
          aria-label="Share this ruling"
          title="Share"
        >
          <IconShare />
        </button>
      )}

      <button
        type="button"
        class={`icon-btn${turn.feedback === "up" ? " is-active" : ""}`}
        onClick={() => rateTurn(turn.id, "up")}
        aria-pressed={turn.feedback === "up"}
        aria-label="Helpful ruling"
        title="Helpful"
      >
        <IconThumbUp />
      </button>

      <button
        type="button"
        class={`icon-btn${turn.feedback === "down" ? " is-active" : ""}`}
        onClick={() => rateTurn(turn.id, "down")}
        aria-pressed={turn.feedback === "down"}
        aria-label="Unhelpful ruling"
        title="Not helpful"
      >
        <IconThumbDown />
      </button>

      {/* Retry re-asks the model in place, so only the newest turn has it. */}
      {isLast && (
        <button
          type="button"
          class="icon-btn"
          disabled={isStreaming.value}
          onClick={() => retryTurn(turn.id)}
          aria-label="Ask this again"
          title="Retry"
        >
          <IconRetry />
        </button>
      )}

      {shareResult && shareResult !== "dismissed" && (
        <span class="answer-actions__status" role="status">
          {STATUS_LABEL[shareResult]}
        </span>
      )}

      {confirming && (
        <ConfirmDialog
          title="Share this ruling?"
          body="This will make the conversation public to anyone with the link."
          confirmLabel="Share"
          busy={busy}
          onCancel={() => {
            if (!busy) setConfirming(false);
          }}
          onConfirm={() => {
            setConfirming(false);
            void doShare();
          }}
        />
      )}
    </div>
  );
}
